import { useEffect, useState } from "react";
import { Plus, Trash2, Tag, Star, Inbox, Upload, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEAD_STATUS = ["new", "contacted", "won", "lost"];

export default function MarketingAdmin() {
  const [coupons, setCoupons] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [cForm, setCForm] = useState({ code: "", discount_type: "percent", value: 10, description: "", active: true });
  const [sForm, setSForm] = useState({ name: "", tier: "Partner", logo_url: "", active: true, is_title: false, banner_url: "" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const load = () => {
    api.get("/admin/coupons").then((r) => setCoupons(r.data));
    api.get("/admin/sponsors").then((r) => setSponsors(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
  };
  useEffect(() => { load(); }, []);

  const addCoupon = async () => {
    try { await api.post("/admin/coupons", { ...cForm, value: +cForm.value }); toast.success("Coupon added"); setCForm({ code: "", discount_type: "percent", value: 10, description: "", active: true }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delCoupon = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  const addSponsor = async () => {
    try { await api.post("/admin/sponsors", sForm); toast.success("Sponsor added"); setSForm({ name: "", tier: "Partner", logo_url: "", active: true, is_title: false, banner_url: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delSponsor = async (id) => { await api.delete(`/admin/sponsors/${id}`); load(); };

  const uploadImg = async (file, setBusy, key) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSForm((f) => ({ ...f, [key]: `${API}/files/${data.path}` }));
      toast.success("Uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const updateLeadStatus = async (id, status) => {
    try { await api.put(`/admin/leads/${id}/status`, { status }); setLeads((ls) => ls.map((l) => l.id === id ? { ...l, status } : l)); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl mb-1">Marketing</h1>
      <p className="text-slate-400 mb-6">Discount coupons and tech-park sponsors.</p>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Coupons */}
        <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Tag className="h-5 w-5 text-violet-400" /> Coupons</h3>
          <div className="space-y-2 mb-4">
            {coupons.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-slate-950/40 px-3 py-2 text-sm" data-testid={`coupon-${c.id}`}>
                <span><span className="font-mono text-amber-400">{c.code}</span> · {c.discount_type === "percent" ? `${c.value}%` : `₹${c.value}`} <span className="text-xs text-slate-500">{c.description}</span></span>
                <button onClick={() => delCoupon(c.id)} className="text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <Input placeholder="CODE" value={cForm.code} onChange={(e) => setCForm({ ...cForm, code: e.target.value.toUpperCase() })} data-testid="coupon-code" className="bg-slate-950/50 border-white/10" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={cForm.discount_type} onValueChange={(v) => setCForm({ ...cForm, discount_type: v })}>
                <SelectTrigger className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
              </Select>
              <Input type="number" placeholder="Value" value={cForm.value} onChange={(e) => setCForm({ ...cForm, value: e.target.value })} className="bg-slate-950/50 border-white/10" />
            </div>
            <Input placeholder="Description" value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} className="bg-slate-950/50 border-white/10" />
            <Button onClick={addCoupon} data-testid="add-coupon" variant="secondary" className="w-full"><Plus className="h-4 w-4 mr-1" /> Add coupon</Button>
          </div>
        </div>

        {/* Sponsors */}
        <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Star className="h-5 w-5 text-amber-400" /> Sponsors</h3>
          <div className="space-y-2 mb-4">
            {sponsors.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg bg-slate-950/40 px-3 py-2 text-sm" data-testid={`sponsor-${s.id}`}>
                <span className="flex items-center gap-2">
                  {s.logo_url && <span className="bg-white/90 rounded px-1 py-0.5"><img src={s.logo_url} alt="" className="h-5 w-auto object-contain" /></span>}
                  <span className="font-display font-bold flex items-center gap-1">{s.is_title && <Crown className="h-3.5 w-3.5 text-amber-400" />}{s.name} <span className="text-xs text-slate-500 font-normal">{s.tier}</span></span>
                </span>
                <button onClick={() => delSponsor(s.id)} className="text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <Input placeholder="Sponsor name" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} data-testid="sponsor-name" className="bg-slate-950/50 border-white/10" />
            <Input placeholder="Tier (Title Partner / Partner)" value={sForm.tier} onChange={(e) => setSForm({ ...sForm, tier: e.target.value })} className="bg-slate-950/50 border-white/10" />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-slate-950/50 cursor-pointer text-sm" data-testid="sponsor-logo-upload">
                {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0], setUploadingLogo, "logo_url")} />
              </label>
              {sForm.logo_url && <span className="bg-white/90 rounded px-1.5 py-1"><img src={sForm.logo_url} alt="" className="h-6 w-auto object-contain" /></span>}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={sForm.is_title} onCheckedChange={(v) => setSForm({ ...sForm, is_title: !!v })} data-testid="sponsor-title-toggle" />
              <span className="flex items-center gap-1"><Crown className="h-3.5 w-3.5 text-amber-400" /> Title sponsor (homepage banner)</span>
            </label>
            {sForm.is_title && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/5 cursor-pointer text-sm" data-testid="sponsor-banner-upload">
                  {uploadingBanner ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Banner image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0], setUploadingBanner, "banner_url")} />
                </label>
                {sForm.banner_url && <img src={sForm.banner_url} alt="" className="h-10 w-auto rounded object-cover" />}
              </div>
            )}
            <Button onClick={addSponsor} data-testid="add-sponsor" variant="secondary" className="w-full"><Plus className="h-4 w-4 mr-1" /> Add sponsor</Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5 mt-6">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Inbox className="h-5 w-5 text-emerald-400" /> Corporate / bulk enquiries <span className="text-xs text-slate-500 font-normal">({leads.length})</span></h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase"><tr>{["Name", "Company", "Email", "Phone", "Size", "Message", "When", "Status"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-white/5" data-testid={`lead-${l.id}`}>
                  <td className="px-3 py-2 font-medium">{l.name}</td>
                  <td className="px-3 py-2 text-slate-400">{l.company}</td>
                  <td className="px-3 py-2 text-violet-300">{l.email}</td>
                  <td className="px-3 py-2 text-slate-400">{l.phone}</td>
                  <td className="px-3 py-2 text-slate-400">{l.team_size}</td>
                  <td className="px-3 py-2 text-slate-400 max-w-[200px] truncate">{l.message}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{l.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2">
                    <Select value={l.status || "new"} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                      <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-8 w-28 bg-slate-950/50 border-white/10 capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAD_STATUS.map((st) => <SelectItem key={st} value={st} className="capitalize">{st}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No enquiries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
